import { FunctionComponent, useEffect, useState } from "react";
import {
  ApproveReplacementRequest,
  ListReplacementRequestsForComplaint,
  RaiseReplacementRequest,
  RejectReplacementRequest,
  RetryDraftReplacementST,
} from "../../../../../service/repos/support/complaint_repo";
import {
  ToastType,
  useToast,
} from "../../../../../context_providers/toast/toast_ctx";
import { useAuth } from "../../../../../context_providers/auth/authProvider";
import { ACTION, MODULE } from "../../../../../utils/enums";
import LoadingWidget from "../../../../components/loading_spinner";

interface ReplacementRequestSummary {
  id: string;
  status: string;
  linked_st_id?: string;
  st_creation_failed?: boolean;
  is_active?: boolean;
  linked_st_status?: string | null;
}

// Read literally from the PRD's allow-list: Resolved is not named as allowed,
// so — like Closed — it blocks the action too.
const RAISABLE_COMPLAINT_STATUSES = [
  "Open",
  "Assigned to Operations",
  "Service in Progress",
];

// The backend computes is_active with the same rule it uses to block a new
// request (so a Cancelled/Delivered linked ST frees the complaint). The
// fallback keeps the old behaviour against a backend that doesn't send it yet.
const isActiveForDisplay = (r: ReplacementRequestSummary) =>
  r.is_active ?? r.status !== "Rejected";

interface Props {
  complaintId?: string;
  complaintStatus?: string;
  canRaise: boolean;
}

const ReplacementPipelineWidget: FunctionComponent<Props> = ({
  complaintId,
  complaintStatus,
  canRaise,
}) => {
  const [loading, setLoading] = useState(false);
  const [raising, setRaising] = useState(false);
  const [actioning, setActioning] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [requests, setRequests] = useState<ReplacementRequestSummary[]>([]);
  const { showToast } = useToast();
  const { isAuthorised } = useAuth();

  const canApprove = isAuthorised({
    module: MODULE.replacement_request_approval,
    action: [ACTION.UPDATE],
  });

  const loadRequests = async () => {
    if (!complaintId) return;
    setLoading(true);
    const res = await ListReplacementRequestsForComplaint(complaintId);
    if (res?.success) setRequests(res.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complaintId]);

  if (!complaintId || loading) return null;

  const active = requests.find(isActiveForDisplay);
  const previous = requests.find(
    (r) => !isActiveForDisplay(r) && r.linked_st_id && r.linked_st_status,
  );

  const handleRaise = async () => {
    setRaising(true);
    const res = await RaiseReplacementRequest(complaintId);
    setRaising(false);
    if (res?.success) {
      showToast({
        type: ToastType.success,
        text: "Replacement request raised and pending approval.",
      });
      loadRequests();
    } else {
      showToast({
        type: ToastType.error,
        text:
          res?.message || res?.error || "Failed to raise replacement request.",
      });
    }
  };

  const handleApprove = async () => {
    if (!active) return;
    setActioning(true);
    const res = await ApproveReplacementRequest(active.id);
    setActioning(false);
    if (res?.success) {
      showToast({
        type: ToastType.success,
        text:
          res?.message || "Request approved and Draft Replacement ST created.",
      });
      loadRequests();
    } else {
      showToast({
        type: ToastType.error,
        text: res?.message || res?.error || "Approval failed.",
      });
    }
  };

  const handleReject = async () => {
    if (!active || !rejectReason.trim()) return;
    setActioning(true);
    const res = await RejectReplacementRequest(active.id, rejectReason.trim());
    setActioning(false);
    if (res?.success) {
      showToast({ type: ToastType.success, text: "Request rejected." });
      setRejecting(false);
      setRejectReason("");
      loadRequests();
    } else {
      showToast({
        type: ToastType.error,
        text: res?.message || res?.error || "Rejection failed.",
      });
    }
  };

  const handleRetry = async () => {
    if (!active) return;
    setActioning(true);
    const res = await RetryDraftReplacementST(active.id);
    setActioning(false);
    if (res?.success) {
      showToast({
        type: ToastType.success,
        text: res?.message || "Draft Replacement ST created.",
      });
      loadRequests();
    } else {
      showToast({
        type: ToastType.error,
        text: res?.message || res?.error || "Retry failed.",
      });
    }
  };

  if (active) {
    return (
      <div
        id="complaint_detail_replacement_pipeline_status"
        className="text-sm rounded border px-3 py-2 bg-gray-50 space-y-2"
      >
        <LoadingWidget loading={actioning} />
        <div>
          Replacement Request:{" "}
          <b>
            {active.st_creation_failed
              ? "Approved — Draft ST creation failed"
              : active.status}
          </b>
          {active.linked_st_id && (
            <span className="ml-2 text-gray-500">
              ST: {active.linked_st_id}
            </span>
          )}
        </div>

        {active.status === "Pending Approval" && canApprove && (
          <>
            {rejecting ? (
              <div className="space-y-2">
                <textarea
                  id="complaint_detail_replacement_request_reject_reason"
                  className="w-full border rounded p-2 text-sm"
                  rows={2}
                  placeholder="Reason for rejection (required)"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
                <div className="flex gap-3">
                  <button
                    id="complaint_detail_replacement_request_reject_submit_btn"
                    type="button"
                    disabled={actioning || !rejectReason.trim()}
                    onClick={handleReject}
                    className="rounded bg-red-600 text-white px-3 py-1 text-sm disabled:opacity-50"
                  >
                    Confirm Reject
                  </button>
                  <button
                    id="complaint_detail_replacement_request_reject_cancel_btn"
                    type="button"
                    disabled={actioning}
                    onClick={() => {
                      setRejecting(false);
                      setRejectReason("");
                    }}
                    className="rounded border px-3 py-1 text-sm disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-3">
                <button
                  id="complaint_detail_replacement_request_approve_btn"
                  type="button"
                  disabled={actioning}
                  onClick={handleApprove}
                  className="rounded bg-green-600 text-white px-3 py-1 text-sm disabled:opacity-50"
                >
                  Approve
                </button>
                <button
                  id="complaint_detail_replacement_request_reject_btn"
                  type="button"
                  disabled={actioning}
                  onClick={() => setRejecting(true)}
                  className="rounded border border-red-400 text-red-600 px-3 py-1 text-sm disabled:opacity-50"
                >
                  Reject
                </button>
              </div>
            )}
          </>
        )}

        {active.st_creation_failed && canApprove && (
          <button
            id="complaint_detail_replacement_request_retry_st_btn"
            type="button"
            disabled={actioning}
            onClick={handleRetry}
            className="rounded border px-3 py-1 text-sm disabled:opacity-50"
          >
            Retry Draft ST Creation
          </button>
        )}
      </div>
    );
  }

  if (
    !canRaise ||
    !complaintStatus ||
    !RAISABLE_COMPLAINT_STATUSES.includes(complaintStatus)
  ) {
    return null;
  }

  return (
    <>
      <LoadingWidget loading={raising} />
      {previous && (
        <div
          id="complaint_detail_replacement_previous_st"
          className="text-sm text-gray-500 mb-1"
        >
          Previous replacement: {previous.linked_st_id} (
          {previous.linked_st_status})
        </div>
      )}
      <button
        id="complaint_detail_raise_replacement_request_btn"
        type="button"
        disabled={raising}
        onClick={handleRaise}
        className="rounded border px-3 py-1 hover:bg-black hover:text-white disabled:opacity-50"
      >
        {raising ? "Raising..." : "Raise Replacement Request"}
      </button>
    </>
  );
};

export default ReplacementPipelineWidget;
